import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users as UsersIcon, FileText, Network, Globe2,
  Download, Settings as Cog, LogOut, Star, Clock, Radio, ShieldCheck, Bell, Search,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useModules } from "../contexts/ModulesContext";
import { Button } from "./ui/button";
import { BRAND_LOGO } from "../lib/personas";
import Avatar from "./Avatar";

const ICONS = {
  dashboard: LayoutDashboard,
  personas: Star,
  users: UsersIcon,
  records: FileText,
  structures: Network,
  geography: Globe2,
  export: Download,
  scheduled: Clock,
  settings: Cog,
  api_webhooks: Radio,
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { isEnabled } = useModules();
  const navigate = useNavigate();
  const location = useLocation();

  const geoEnabled = isEnabled("pays") || isEnabled("regions") || isEnabled("sites");
  const mrEnabled = isEnabled("multi_record");

  const groups = [
    {
      heading: "Navigation",
      items: [
        { to: "/", key: "dashboard", label: "Tableau de bord", icon: ICONS.dashboard },
        { to: "/personas", key: "personas", label: "Personas démo", icon: ICONS.personas },
        { to: "/users", key: "users", label: "Collaborateurs", icon: ICONS.users },
        ...(mrEnabled ? [{ to: "/user-records", key: "records", label: "Contrats", icon: ICONS.records }] : []),
        { to: "/structures", key: "structures", label: "Structures", icon: ICONS.structures },
        ...(geoEnabled ? [{ to: "/geography", key: "geography", label: "Géographie", icon: ICONS.geography }] : []),
      ],
    },
    {
      heading: "Exports",
      items: [
        { to: "/export", key: "export", label: "Exports manuels", icon: ICONS.export },
        { to: "/scheduled-exports", key: "scheduled", label: "Exports planifiés", icon: ICONS.scheduled },
      ],
    },
    {
      heading: "Paramètres",
      items: [
        { to: "/settings", key: "settings", label: "Référentiels", icon: ICONS.settings },
        { to: "/webhooks", key: "api_webhooks", label: "API & Webhooks", icon: ICONS.api_webhooks },
      ],
    },
  ];

  return (
    <div className="h-screen flex overflow-hidden min-w-[1280px] bg-cream">
      {/* Sidebar */}
      <aside className="w-64 border-r border-sand bg-off-white flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-sand">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-terracotta flex items-center justify-center shadow-soft">
              <img src={BRAND_LOGO} alt="" className="w-7 h-7 logo-white object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[18px] font-semibold text-warm-dark">Maisonéa</div>
              <div className="text-[10px] text-warm-dark/60 tracking-[0.18em] uppercase font-medium">SIRH</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {groups.map((g) => (
            <div key={g.heading}>
              <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-warm-dark/50 px-3 mb-1.5">
                {g.heading}
              </div>
              <div className="space-y-0.5">
                {g.items.map((it) => {
                  const Icon = it.icon;
                  const active = location.pathname === it.to || (it.to !== "/" && location.pathname.startsWith(it.to));
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.to === "/"}
                      data-testid={`nav-${it.key}`}
                      className={`flex items-center gap-3 text-[14px] rounded-lg px-3 py-2 text-warm-dark/80 hover:bg-terracotta-light/40 hover:text-warm-dark transition-colors ${active ? "nav-item-active font-medium" : ""}`}
                    >
                      <Icon size={16} strokeWidth={1.5} className="shrink-0" />
                      <span>{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-sand p-3">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg bg-off-white">
            <Avatar name={user?.name} email={user?.email} seed={user?.persona || user?.email || "u"} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-warm-dark truncate">{user?.name || user?.email}</div>
              <div className="text-[11px] text-warm-dark/60 truncate">{user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
              className="text-warm-dark/60 hover:text-terracotta p-1.5 rounded-md"
              data-testid="btn-logout"
              aria-label="Déconnexion"
            >
              <LogOut size={15} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <div className="h-14 border-b border-sand bg-off-white/60 backdrop-blur flex items-center justify-end px-6 gap-1" data-testid="topbar">
          <button className="w-9 h-9 rounded-lg hover:bg-terracotta-light/40 flex items-center justify-center text-warm-dark/70" aria-label="Recherche" data-testid="topbar-search">
            <Search size={16} strokeWidth={1.5} />
          </button>
          <button className="w-9 h-9 rounded-lg hover:bg-terracotta-light/40 flex items-center justify-center text-warm-dark/70 relative" aria-label="Notifications" data-testid="topbar-notifs">
            <Bell size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => navigate("/admin")}
            className="w-9 h-9 rounded-lg hover:bg-terracotta-light/40 flex items-center justify-center text-warm-dark/70"
            aria-label="Admin & Logs"
            data-testid="topbar-admin"
          >
            <ShieldCheck size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="main-content">
          {children}
        </div>
      </main>
    </div>
  );
}
